import type { Toast } from './types';

type Listener = (toasts: Toast[]) => void;

class ToastManager {
  private toasts: Toast[] = [];
  private listeners: Set<Listener> = new Set();
  private timeoutRefs: Map<string, NodeJS.Timeout> = new Map();
  private maxToasts = 3;
  private defaultDuration = 5000;

  configure(options: { maxToasts?: number; defaultDuration?: number }) {
    if (options.maxToasts !== undefined) {
      this.maxToasts = options.maxToasts;
    }
    if (options.defaultDuration !== undefined) {
      this.defaultDuration = options.defaultDuration;
    }
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    this.listeners.forEach((listener) => listener([...this.toasts]));
  }

  toast(messageOrOptions: string | Omit<Toast, 'id'>): string {
    const options: Omit<Toast, 'id'> =
      typeof messageOrOptions === 'string'
        ? { message: messageOrOptions }
        : messageOrOptions;

    const id = Date.now().toString() + Math.random().toString(36);
    const duration = options.duration ?? this.defaultDuration;

    const newToast: Toast = {
      id,
      ...options,
    };

    this.toasts = [...this.toasts, newToast];

    if (this.toasts.length > this.maxToasts) {
      const removedToast = this.toasts.shift();
      if (removedToast) {
        const timeout = this.timeoutRefs.get(removedToast.id);
        if (timeout) {
          clearTimeout(timeout);
          this.timeoutRefs.delete(removedToast.id);
        }
      }
    }

    this.notify();

    if (duration > 0) {
      const timeout = setTimeout(() => {
        this.dismiss(id);
      }, duration);
      this.timeoutRefs.set(id, timeout);
    }

    return id;
  }

  dismiss(id: string) {
    const timeout = this.timeoutRefs.get(id);
    if (timeout) {
      clearTimeout(timeout);
      this.timeoutRefs.delete(id);
    }

    const toast = this.toasts.find((t) => t.id === id);

    this.toasts = this.toasts.filter((t) => t.id !== id);
    this.notify();

    toast?.onDismiss?.();
  }

  dismissAll() {
    this.timeoutRefs.forEach((timeout) => clearTimeout(timeout));
    this.timeoutRefs.clear();
    this.toasts = [];
    this.notify();
  }

  getDefaultDuration() {
    return this.defaultDuration;
  }

  getToasts() {
    return [...this.toasts];
  }

  success(message: string, description?: string) {
    return this.toast({
      message,
      description,
      variant: 'success',
    });
  }

  error(message: string, description?: string) {
    return this.toast({
      message,
      description,
      variant: 'error',
    });
  }

  info(message: string, description?: string) {
    return this.toast({
      message,
      description,
      variant: 'info',
    });
  }
}

export const toastManager = new ToastManager();

export const toast = toastManager.toast.bind(toastManager);
export const dismiss = toastManager.dismiss.bind(toastManager);
export const dismissAll = toastManager.dismissAll.bind(toastManager);

export default {
  toast,
  success: toastManager.success.bind(toastManager),
  error: toastManager.error.bind(toastManager),
  info: toastManager.info.bind(toastManager),
  dismiss,
  dismissAll,
};
